/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { Filter } from '../../../model/Filter';
import { Equipment } from '../../../model/Equipment';
import { BaseController } from '../../BaseController';
import { EquipmentAttachmentViewController } from '../../equipment/component/EquipmentAttachmentView';

class EquipmentViewController extends BaseController {
    /** @ngInject */
    constructor(configuration, equipmentService, equipmentTemplateService, siteService, companyService,
        equipmentAttachmentService, waitSpinnerService, $q, $state, $stateParams, $log, notificationService,
        dateTimeService, localizationService, $scope, filterService, dialogService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.equipmentService = equipmentService;
        this.equipmentTemplateService = equipmentTemplateService;
        this.equipmentAttachmentService = equipmentAttachmentService;
        this.siteService = siteService;
        this.companyService = companyService;
        this.dateTimeService = dateTimeService;
        this.localizationService = localizationService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$scope = $scope;
        this.defaultZoom = 5;
        this.focusZoom = 16;
        this.center = {};
        angular.extend(this.$scope, {
            center: this.center,
            defaults: {
                scrollWheelZoom: false
            }
        });
        // Moves the map so as the marker is always at the center after the user drags it.
        this.listenerUnregistrationHooks = [];
        this.listenerUnregistrationHooks.push(this.$scope.$on(
            "leafletDirectiveMarker.dragend",
            (event, args) => this.applyLocation(args.model.lat, args.model.lng)
        ));
        this.$log.debug('Controller \'EquipmentView\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.formDisabled = false;
        this.equipmentTemplates = [];
        this.sites = [];
        this.equipmentMarker = {
            draggable: true,
            icon: {
                iconUrl: 'images/marker.png',
                iconSize: [25, 41],
                iconAnchor: [12, 40]
            }
        }

        // The updateMode flag can be used to know the context in which this controller is running: creation or update.
        let initPromise;
        let equipmentTemplatePromise;
        if (this.$stateParams.hasOwnProperty('id')) {
            this.updateMode = true;
            this.title = 'ui.equipment.update';
            initPromise = this.equipmentService.getEntity(this.$stateParams.id);
            equipmentTemplatePromise = initPromise.then(equipment => this.getEquipmentTemplates(equipment.domainId));
        } else {
            this.updateMode = false;
            this.title = 'ui.equipment.creation';
            const equipment = new Equipment();
            equipment.location = {
                longitude: 2.1,
                latitude: 47
            };
            initPromise = this.$q.resolve(equipment);
            equipmentTemplatePromise = [];
        }

        this.commands = [];
        this.handleRestServiceCall(this.$q.all({
            entity: initPromise,
            sites: this.siteService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            }),
            equipmentTemplates: equipmentTemplatePromise,
            companies: this.companyService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'}),
                data: this.filterService.buildFilters([Filter.equalFilter('enabled', true)])
            })
        }), data => {
            if (data.entity && angular.isString(data.entity.inServiceDate)) {
                data.entity.inServiceDate = this.dateTimeService.parseDate(data.entity.inServiceDate);
                data.entity.remainingWarrantyPeriod = this.dateTimeService.getRemainingMonths(data.entity.inServiceDate, data.entity.warrantyPeriod);
            }
            this.pristineEntity = new Equipment(data.entity);
            this.reset();
            this.equipmentTemplates = data.equipmentTemplates;
            this.sites = data.sites;
            this.companies = data.companies;
            this.initCommands();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    $onDestroy() {
        // Unregisters all listeners from the root scope, to avoid multi-calls when the component will be loaded again.
        for (const listenerUnregistrationHook of this.listenerUnregistrationHooks) {
            listenerUnregistrationHook();
        }
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        const stateParams = {
            filter: {
                domainId: this.pristineEntity.domainId,
                siteId: this.pristineEntity.siteId,
                equipmentId: this.pristineEntity.id
            }
        };
        this.commands.push(
            Command.stateCommand('ui.alarm.list', 'images/svg/alarm.svg', 'gmao.alarm.list',
                ['ADMIN', 'ALARM_READ'], stateParams),
            Command.stateCommand('ui.actionrequest.list', 'images/svg/wrench.svg', 'gmao.actionrequest.list',
                ['ADMIN', 'ACTION_REQUEST_READ'], stateParams),
            Command.stateCommand('ui.preventivesheet.list', 'images/svg/prevention.svg',
                'gmao.preventivesheet.list', ['ADMIN', 'PREVENTIVE_SHEET_READ'], stateParams)
        );
        if (this.updateMode) {
            this.commands.push(
                Command.handlerCommand('ui.site.addattachments', 'images/svg/attachment.svg',
                    () => this.showAttachmentViewDialog(), ['ADMIN', 'EQUIPMENT_WRITE'])
            );
        }
    }

    /**
     * Loads equipment templates in the same domain than the given site.
     *
     * @param {Number} siteId Site ID.
     */
    loadEquipmentTemplates(siteId) {
        if (siteId) {
            this.equipmentTemplatesDisabled = true;
            const domainId = this.sites.find(site => site.id === siteId).domainId;
            this.handleRestServiceCall(
                this.getEquipmentTemplates(domainId),
                equipmentTemplates => {
                    this.equipmentTemplates = equipmentTemplates;
                    // If the selected template is not part of the preventive sheet templates loaded, reset it.
                    if (!this.equipmentTemplates.map(equipmentTemplate => equipmentTemplate.id)
                        .includes(this.equipment.equipmentTemplateId)) {
                        this.equipment.equipmentTemplateId = undefined;
                    }
                }
            ).catch(() => {
                this.notificationService.pushMessage('ui.equipment.equipmenttemplateloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            }).finally(() => {
                this.equipmentTemplatesDisabled = false;
            });
        }
    }

    /**
     * Gets equipment templates related to a domain.
     *
     * @param {Number} domainId Domain ID.
     * @return {Promise} Promise containing the templates loaded.
     */
    getEquipmentTemplates(domainId) {
        return this.equipmentTemplateService.findEntities({
            params: this.filterService.buildSortingParam({sort: 'name'}),
            data: this.filterService.buildFilters([Filter.equalFilter('domain.id', domainId)])
        });
    }

    showEquipmentTemplateChangeWarning() {
        if (this.updateMode && (this.equipment.equipmentTemplateId !== this.pristineEntity.equipmentTemplateId)) {
            this.dialogService.showMessageDialog('ui.equipment.templatechangewarning');
        }
    }

    /**
     * Apply the given longitude and latitude to both equipment and view. Optionally, apply a zoom on the map.
     *
     * @param {Float} latitude Latitude.
     * @param {Float} longitude Longitude.
     * @param {Integer} [zoom] Zoom level (from 1 to 18).
     */
    applyLocation(latitude, longitude, zoom) {
        this.applyLatitude(latitude);
        this.applyLongitude(longitude);
        if (zoom) {
            this.center.zoom = zoom;
        }
    }

    applyLatitude(latitude) {
        const updatedLatitude = latitude || this.equipment.location.latitude;
        // Keep the map always centered at the marker's location.
        this.center.lat = updatedLatitude;
        this.equipmentMarker.lat = updatedLatitude;
        this.equipment.location.latitude = updatedLatitude;
        return this.equipment.location.latitude;
    }

    applyLongitude(longitude) {
        const updatedLongitude = longitude || this.equipment.location.longitude;
        // Keep the map always centered at the marker's location.
        this.center.lng = updatedLongitude;
        this.equipmentMarker.lng = updatedLongitude;
        this.equipment.location.longitude = updatedLongitude;
        return this.equipment.location.longitude;
    }

    findLocationByAddress() {
        this.localizationService.findLocationByAddress(this.equipment.address).then(location => {
            this.applyLocation(location.latitude, location.longitude, this.focusZoom);
        }).catch(() => {
            this.notificationService.pushMessage('ui.equipment.unknownaddress',
                this.configuration.NOTIFIER_LEVEL_ERROR);
        });
    }

    /**
     * Maps an equipment object to the input format expected by the remote API, before a save request can be sent.
     *
     * @param {object} equipment An equipment instance.
     */
    mapToDto(equipment) {
        const convertedEquipment = angular.copy(equipment);
        convertedEquipment.inServiceDate = this.dateTimeService.formatDate(equipment.inServiceDate);
        return convertedEquipment;
    }

    /**
     * Shows a dialog containing the form to add attachments. When the dialog is closed, the view is refreshed.
     *
     * @return {Promise} A promise of the action.
     */
    showAttachmentViewDialog() {
        this.dialogService.showDialog(EquipmentAttachmentViewController,
            'equipment/component/equipmentattachment-view.html', { equipmentId: this.equipment.id }
        ).then(confirmed => {
            if (confirmed) {
                this.$onInit();
            }
        });
    }

    /**
     * Shows a confirmation dialog before deleting an attachment. If the dialog is confirmed, the attachment is deleted.
     *
     * @param {integer} id ID of the attachment.
     * @return {Promise} A promise containing the confirmation result.
     */
    showAttachmentDeleteConfirmationDialog(id) {
        return this.dialogService.showConfirmationDialogWithMessage('ui.attachment.deleteconfirmation').then(
            confirmed => {
                if (confirmed) {
                    this.deleteAttachment(id);
                }
                return confirmed;
            }
        );
    }

    /**
     * Deletes immediately an attachment without confirmation, and reloads the site.
     *
     * @param {integer} id ID of the attachment.
     */
    deleteAttachment(id) {
        return this.handleRestServiceCall(this.equipmentAttachmentService.deleteEntity({equipmentId: this.equipment.id, id}), () => {
            this.notificationService.pushMessage('ui.attachment.deleted',
                this.configuration.NOTIFIER_LEVEL_INFO);
            this.$onInit();
        });
    }

    save() {
        this.formDisabled = true;
        const dto = this.mapToDto(this.equipment);
        let serviceActionPromise;
        if (this.updateMode) {
            serviceActionPromise = this.equipmentService.updateEntity(dto);
        } else {
            serviceActionPromise = this.equipmentService.createEntity(dto);
        }
        this.handleRestServiceCall(serviceActionPromise, () => {
            const message = 'ui.equipment.' + (this.updateMode ? 'updated' : 'created');
            this.notificationService.pushMessage(message, this.configuration.NOTIFIER_LEVEL_INFO);
            if (this.updateMode) {
                this.$state.reload();
            } else {
                this.$state.go('gmao.equipment.list');
            }
        }).finally(() => {
            this.formDisabled = false;
        });
    }

    /**
     * Restores the initial state of the view, and the map.
     */
    reset() {
        this.equipment = angular.copy(this.pristineEntity);
        this.applyLocation(this.equipment.location.latitude, this.equipment.location.longitude,
            this.updateMode ? this.focusZoom : this.defaultZoom);
        angular.extend(this.$scope, {
            markers: {
                equipmentMarker: this.equipmentMarker
            },
            center: this.center,
            defaultIcon: {},
            events: {
                markers:{
                    enable: [ 'dragend' ],
                    logic: 'emit'
                }
            }
        });
    }
}

export const equipmentViewComponent = {
    template: require('./equipment-view.html'),
    controller: EquipmentViewController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/equipment/component/EquipmentView.js